import SystemMessage from "../components/system_message";
import NavBar from "../components/nav_bar";
import youGenerateIcon from "../assets/images/you_generate.png";
import Notepad from "../components/notepad";
import SaveInfoTooltip from "../components/save_info_tooltip";
import { secrets, useNotepadStore } from "../store/notepad";
import { useEffect, useRef, useState } from "react";
import t from "../locales";

const basePasswords = t("yougenerate_basic_passwords");

const STATES = {
  START: 1,
  WORKING: 2,
  SUCCESS: 3,
  FAILURE: 4,
};

function randomNumber(min, max) {
  return Math.floor(Math.random() * (max - min) + min);
}

function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

function generatePasswordCombinations(knownSecrets) {
  return knownSecrets
    .map((s1, i) => {
      const l1 = [...knownSecrets, ""];
      l1.splice(i, 1);
      return l1.map((s2, ii) => {
        const l2 = [...l1, ""];
        l2.splice(ii, 1);
        return l2.map((s3) => [s1, s2, s3]);
      });
    })
    .flat(2);
}

function generatePasswords(knownSecrets) {
  function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  const combinations = generatePasswordCombinations(
    knownSecrets.map((secret) => secret.replace(" ", "_")),
  );
  const passwords = combinations.flatMap(([s1, s2, s3]) => [
    `${s1.toUpperCase()}@${s2}@${s3}`,
    `${s1.toUpperCase()}123${s2}!${s3}`,
    `w@chtw00rd${s2.toUpperCase()}${s3}!`,
    `w@chtw00rd${s3}abc`,
    `${capitalize(s1)}_${capitalize(s2)}@_${capitalize(s3)}`,
    `${s3}x${s2}x${s1}`,
    `azerty${s2}_${capitalize(s3)}`,
    `1111${s2.toUpperCase()}`,
    `love${s2.toUpperCase()}!`,
    `123${s1}${s2}321`,
    `${s1}_${s2}xyz`,
    `${s1.toUpperCase()}@${s2}@${s3}`,
  ]);
  return shuffle(
    shuffle(passwords)
      .slice(0, 35)
      .concat(shuffle(basePasswords).slice(0, 40 - passwords.length)),
  );
}

export default function youGenerate() {
  const [state, setState] = useState(STATES.START);
  const [generatedPasswords, setGeneratedPassswords] = useState([]);
  const foundSecrets = useNotepadStore((state) => state.secrets);
  const knownSecrets = Object.entries(foundSecrets).reduce((acc, [k, v]) => {
    if (v && secrets[k]) acc.push(secrets[k].value);
    return acc;
  }, []);

  const handleStart = (success, selectedSecrets) => {
    setGeneratedPassswords([]);
    setState(STATES.WORKING);

    const options = generatePasswords(selectedSecrets);
    if (success) options.push(t("secrets_password_value"));

    options.forEach((pw, i) => {
      setTimeout(
        () => setGeneratedPassswords((arr) => [...arr, pw]),
        i * 100 + randomNumber(0, 75),
      );
    });
    setTimeout(
      () => setState(success ? STATES.SUCCESS : STATES.FAILURE),
      (options.length + 10) * 100,
    );
  };

  return (
    <>
      <SystemMessage text={t("yougenerate_system_message")} />
      <NavBar icon={youGenerateIcon} />
      {state === STATES.START ? (
        <StartScreen knownSecrets={knownSecrets} onStart={handleStart} />
      ) : (
        <ResultsScreen
          generatedPasswords={generatedPasswords}
          state={state}
          resetState={() => setState(STATES.START)}
        />
      )}
      <Notepad />
    </>
  );
}

function StartScreen({ knownSecrets, onStart }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData(e.target);
    const success =
      ["birthday", "holiday", "pet", "sportteam", "city"]
        .map((key) => {
          return secrets[key].value === data.get(key);
        })
        .filter((x) => x === false).length === 0;
    onStart(success, [...data.values()]);
  };

  return (
    <form className="you-generate" onSubmit={handleSubmit}>
      <h1 className="you-generate__title">{t("yougenerate_title")}</h1>
      {Object.entries(secrets)
        .filter(([key]) => !["password", "email"].includes(key))
        .map(([key, value]) => (
          <div className="you-generate__select-container " key={value.label}>
            <label htmlFor={key} className="you-generate__label">
              {value.label}
            </label>
            <select
              name={key}
              id={key}
              defaultValue=""
              className="you-generate__select"
              disabled={knownSecrets.length === 0}
            >
              <option value="" disabled></option>
              {knownSecrets.map((secret) => (
                <option key={secret} value={secret}>
                  {secret}
                </option>
              ))}
            </select>
          </div>
        ))}
      <button className="button you-generate__button">
        {t("yougenerate_button")}
      </button>
    </form>
  );
}

function ResultsScreen({ state, generatedPasswords, resetState }) {
  const passwordsContainer = useRef();

  useEffect(() => {
    passwordsContainer.current.scrollTop =
      passwordsContainer.current.scrollHeight;
  }, [generatedPasswords]);

  return (
    <div className="you-generate you-generate--results">
      <div className="you-generate__passwords" ref={passwordsContainer}>
        {generatedPasswords.map((pw, i) => (
          <DisplayPassword password={pw} key={`${i}-${pw}`} />
        ))}
      </div>
      {state === STATES.SUCCESS && (
        <p className="you-generate__correct">
          <SaveInfoTooltip name="password">
            {t("secrets_password_value")}
          </SaveInfoTooltip>
        </p>
      )}
      {state === STATES.FAILURE && (
        <>
          <p className="you-generate__error">{t("yougenerate_error_1")}</p>
          <p className="you-generate__error">{t("yougenerate_error_2")}</p>
          <button className="button you-generate__button" onClick={resetState}>
            {t("yougenerate_try_again")}
          </button>
        </>
      )}
    </div>
  );
}

function DisplayPassword({ password = "" }) {
  return <p className="you-generate__password">{password}</p>;
}
